import { FC, ReactNode, useState, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { ClockIcon } from '@phosphor-icons/react';
import { useTranslation } from 'next-i18next';
import styles from './Tag.module.scss';

export interface TagProps {
  /**
   * The color variant of the tag. When omitted, no bottom border accent is applied.
   */
  color?: 'blue' | 'green' | 'gray' | 'discontinued';
  /**
   * Size of the tag
   * @default 'medium'
   */
  size?: 'small' | 'medium';
  /**
   * Whether the tag appears on a dark background
   * @default false
   */
  onDarkBg?: boolean;
  /**
   * Label text to display
   */
  children?: ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

// Base Tag component
const Tag: FC<TagProps> = ({
  color,
  size = 'medium',
  onDarkBg = false,
  children,
  className,
  'data-testid': dataTestId,
}) => {
  return (
    <span
      className={cn(
        styles.root,
        color && styles[color],
        styles[size],
        onDarkBg && styles.onDarkBg,
        className
      )}
      data-testid={dataTestId}
    >
      {children}
    </span>
  );
};

// Semantic wrapper components
export interface SemanticTagProps {
  size?: 'small' | 'medium';
  onDarkBg?: boolean;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * Tag for new products (blue accent)
 */
export const NewTag: FC<SemanticTagProps> = (props) => (
  <Tag color="blue" {...props} />
);

/**
 * Tag for MEC-specific labels like "MEC PICK", "EXCLUSIVE TO MEC", "MEMBER REWARDS" (green accent)
 */
export const MecTag: FC<SemanticTagProps> = (props) => (
  <Tag color="green" {...props} />
);

/**
 * Tag for generic labels like "TRENDING", "TOP SELLER", "BEST DEAL", "ONLINE ONLY" (gray accent)
 */
export const GenericTag: FC<SemanticTagProps> = (props) => (
  <Tag color="gray" {...props} />
);

/**
 * Tag for discontinued products (gray with muted text)
 */
export const DiscontinuedTag: FC<SemanticTagProps> = (props) => (
  <Tag color="discontinued" {...props} />
);

// Counter component
export interface CounterProps {
  count: number;
  className?: string;
  'data-testid'?: string;
}

/**
 * Counter badge for displaying numeric counts (e.g., cart items)
 */
export const Counter: FC<CounterProps> = ({
  count,
  className,
  'data-testid': dataTestId,
}) => {
  return (
    <span className={cn(styles.counter, className)} data-testid={dataTestId}>
      {count}
    </span>
  );
};

// Sale Timer component
export interface SaleTimerProps {
  /**
   * The end date/time for the sale (Date object or ISO string)
   */
  endDate: Date | string;
  size?: 'small' | 'medium';
  onDarkBg?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Formats a time value as two digits (e.g., 5 -> "05")
 */
const padTime = (value: number): string => {
  return value.toString().padStart(2, '0');
};

/**
 * Calculates time remaining and formats as HH:MM:SS
 */
const getTimeRemaining = (endDate: Date): string | null => {
  const now = new Date().getTime();
  const end = endDate.getTime();
  const timeLeft = end - now;

  if (timeLeft <= 0) {
    return null;
  }

  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

  return `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;
};

/**
 * Sale countdown timer with clock icon that counts down in real-time
 */
export const SaleTimer: FC<SaleTimerProps> = ({
  endDate,
  size = 'medium',
  onDarkBg = false,
  className,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation('common');
  const iconSize = size === 'small' ? 12 : 16;

  // Convert string to Date if needed and memoize to prevent dependency changes
  const endDateTime = useMemo(
    () => (typeof endDate === 'string' ? new Date(endDate) : endDate),
    [endDate]
  );

  // State for countdown
  const [timeRemaining, setTimeRemaining] = useState<string | null>(
    getTimeRemaining(endDateTime)
  );

  useEffect(() => {
    // Update countdown every second
    const interval = setInterval(() => {
      const remaining = getTimeRemaining(endDateTime);
      setTimeRemaining(remaining);

      // Clear interval when timer expires
      if (remaining === null) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endDateTime]);

  // Show expired message if time has run out
  const displayText = timeRemaining
    ? `${t('saleTimer.endsIn')} ${timeRemaining}`
    : t('saleTimer.expired');

  return (
    <span
      className={cn(
        styles.saleTimer,
        styles[size],
        onDarkBg && styles.onDarkBg,
        className
      )}
      data-testid={dataTestId}
    >
      <ClockIcon size={iconSize} weight="regular" />
      <span className={styles.timerText}>{displayText}</span>
    </span>
  );
};

export default Tag;
